import axios from 'axios'
import { Language } from '../constants'

const STRAPI_QUERY = `
data {
  id
  attributes {
    locale
    title
    slug
    metaData
    seo {
      metaTitle
      metaDescription
      keywords
      canonicalURL
      metaViewport
      structuredData
      metaImage {
        data {
          attributes {
            url
          }
        }
      }
      metaSocial {
        title
        description
        socialNetwork
        image {
          data {
            attributes {
              url
            }
          }
        }
      }
    }
    blocks {
      __typename
      ... on ComponentBlocksGenericArticleContentType {
        id
        genericArticleContentType: contentType
        pageSize
      }
      ... on ComponentBlocksContent {
        id
        content
        displayRules {
          id
          authentication
        }
      }
      ... on ComponentBlocksLogin {
        id
        title
        description
        isClientLogin
      }
      ... on ComponentBlocksNewsletterSubscriptions {
        id
        title
        levelIdsAsString: levelIds
        displayRules {
          id
          authentication
        }
        feedIds(pagination: { limit: 999 }) {
          id
          name
          description
          feedId
        }
      }
      ... on ComponentBlocksUserSubscriptions {
        id
        userSubscriptionsTitle: title
        displayRules {
          id
          authentication
        }
        levelIds(pagination: { limit: 999 }) {
          id
          name
          description
          levelId
        }
      }
      ... on ComponentBlocksEmptyLine {
        id
        height
      }
      ... on ComponentBlocksDossier {
        id
        slug
        timeFrame
        displayRules {
          id
          authentication
        }
      }
      ... on ComponentBlocksContentGrid {
        id
        gridGap
        minContentWidth
        minColumns
        maxColumns
        contentIsSpacedEvenly
        gridContent(pagination: { limit: 999 }) {
          id
          content
        }
      }
      ... on ComponentBlocksScript {
        id
        source
        javascript
        scriptContent: content
      }
    }
    sideBar {
      __typename
      ... on ComponentSidebarBlocksJustIn {
        id
        displayRules {
          id
          authentication
        }
      }
      ... on ComponentSidebarBlocksAdGroup {
        id
        displayRules {
          id
          authentication
        }
      }
      ... on ComponentSidebarBlocksSubscribe {
        id
        displayRules {
          id
          authentication
        }
      }
      ... on ComponentSidebarBlocksLiveBlogSummary {
        id
        displayRules {
          id
          authentication
        }
        articleLimit
      }
      ... on ComponentSidebarBlocksTopStories {
        id
        displayRules {
          id
          authentication
        }
        articleLimit
      }
      ... on ComponentSidebarBlocksContentType {
        id
        displayRules {
          id
          authentication
        }
        contentType
      }
      ... on ComponentBlocksContent {
        id
        content
        displayRules {
          id
          authentication
        }
      }
    }
  }
}
`

const getAppRoutes = async () => {
  const { data } = await axios.post(process.env.GRAPHQL_API, {
    query: `
    {
      pages {
        nodes {
          id
          title
          titleNl
          description
          descriptionNl
          url
          urlNl
          websiteTexts {
            html
            languageId
          }
        }
      }
    }`
  })

  const pageResult = data?.data?.pages?.nodes ?? []

  const pages = pageResult
    .filter((x) => x.url)
    .map((x) => ({
      route: `/page${x.url}`,
      payload: {
        id: x.id,
        title: x.title,
        description: x.description,
        url: x.url,
        languageId: x.languageId,
        websiteTexts: x.websiteTexts.filter((y) => y.languageId === Language.ENGLISH),
        altLangRoute: `/pagina${x.urlNl}`
      }
    }))

  const paginas = pageResult
    .filter((x) => x.urlNl)
    .map((x) => ({
      route: `/pagina${x.urlNl}`,
      payload: {
        id: x.id,
        title: x.titleNl,
        description: x.descriptionNl,
        url: x.urlNl,
        languageId: x.languageId,
        websiteTexts: x.websiteTexts.filter((y) => y.languageId === Language.DUTCH),
        altUrl: x.url,
        altLangRoute: `/page${x.url}`
      }
    }))

  return [...pages, ...paginas]
}

const getStrapiRoutes = async () => {
  const { data } = await axios.post(
    process.env.GRAPHQL_STRAPI_API,
    {
      query: `
      {
        pages(locale: "all", pagination: { limit: 999 }, publicationState: ${process.env.STRAPI_PUBLICATION_STATE}) {
          ${STRAPI_QUERY}
        }
      }`
    },
    {
      headers: {
        Authorization: `Bearer ${process.env.STRAPI_API_TOKEN}`
      }
    }
  )

  return data.data.pages.data
    .map((page) => {
      const hasBothEngAndNld = data.data.pages.data.filter((p) => p.attributes.slug === page.attributes.slug).length > 1
      if (hasBothEngAndNld) {
        return {
          route: page.attributes.locale === 'en' ? page.attributes.slug : '/nld' + page.attributes.slug,
          payload: page
        }
      } else {
        return [
          {
            route: page.attributes.slug,
            payload: page
          },
          {
            route: '/nld' + page.attributes.slug,
            payload: page
          }
        ]
      }
    })
    .flat()
}

const getRoutes = async () => {
  const appRoutes = await getAppRoutes()
  const strapiRoutes = await getStrapiRoutes()
  return [...appRoutes, ...strapiRoutes]
}

export default {
  getRoutes,
  STRAPI_QUERY
}
